import { defineStore } from 'pinia';
import { apiCall } from '@/utils/globalFunctions';
import store from '@/store/store';
import { debounce } from 'lodash';
import { useActivityStore } from '@/store/activity';
import { useSubscriptionStore } from '@/store/subscription';
// Helper functions
function getPersistedState(storageKey) {
  try {
    const stored = localStorage.getItem(storageKey);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.warn('Failed to read localStorage for companiesStore', error);
    return {};
  }
}

function setPersistedState(storageKey, state) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to persist companies state', error);
  }
}

export const useCompanyStore = defineStore('companies', {
  state: () => {
    const userId = store.state.user?.user_id || null;
    const storageKey = userId ? `companiesStore_${userId}` : 'companiesStore';
    const persisted = getPersistedState(storageKey);
    return {
      userId,
      companies: persisted.companies || [],
      currentPage: persisted.currentPage || 1,
      itemsPerPage: persisted.itemsPerPage || 10,
      totalPages: persisted.totalPages || 1,
      totalRows: persisted.totalRows || 0,
      searchQuery: persisted.searchQuery || '',
      currentCompany: persisted.currentCompany || null,
      loading: false,
      error: null,
      useLocalFiltering: true,
    };
  },

  getters: {
     filteredCompanies: (state) => {
      // If using local filtering, apply filters to the existing data
      if (state.useLocalFiltering) {
        let filtered = state.companies;
        
        if (state.searchQuery) {
          const query = state.searchQuery.toLowerCase();
          filtered = filtered.filter(company =>
            company.company_name?.toLowerCase().includes(query)
          );
        }
        
        return filtered;
      }
      // Otherwise return all companies (assuming API will handle filtering)
      return state.companies;
    },
    
    paginatedCompanies: (state) => {
      const filtered = state.filteredCompanies;
      const start = (state.currentPage - 1) * state.itemsPerPage;
      const end = start + state.itemsPerPage;
      return filtered.slice(start, end);
    },
    
    totalFilteredRows: (state) => state.filteredCompanies.length,
    
    activeCompanies: (state) => state.companies.filter((c) => c.is_active),
  },

  actions: {
    // State management
    persistState() {
      setPersistedState(`companiesStore_${this.userId}`, {
        companies: this.companies,
        currentPage: this.currentPage,
        itemsPerPage: this.itemsPerPage,
        totalPages: this.totalPages,
        totalRows: this.totalRows,
        searchQuery: this.searchQuery,
        currentCompany: this.currentCompany,
      });
    },

    setUserId(userId) {
      this.userId = userId;
      this.reset();
      if (userId) {
        this.fetchCompanies();
      }
    },

    reset() {
      this.$reset();
      if (this.userId) {
        localStorage.removeItem(`companiesStore_${this.userId}`);
      }
    },

    resetFilters() {
      this.searchQuery = '';
      this.currentPage = 1;
      this.useLocalFiltering = true; // Reset to local filtering
    },

    setSearchQuery: debounce(function(query) {
      this.searchQuery = query;
      this.currentPage = 1;
      
      // Always use local filtering for search
      this.useLocalFiltering = true;
      
      // Only trigger API call if we have specific filters (none in this case)
      // For "All" companies, search is always local
    }, 300),

    clearSearch() {
      this.searchQuery = '';
      this.currentPage = 1;
      this.useLocalFiltering = true;
    },

    setError(err, fallbackMessage, fallbackCode) {
      this.error = {
        message: err?.message || fallbackMessage,
        code: err?.code || fallbackCode,
        timestamp: new Date().toISOString(),
      };
    },

    resetError() {
      this.error = null;
    },

    async fetchCompanies() {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
        return;
      }
      this.loading = true;
      this.resetError();

      try {
        const response = await apiCall({
          method: 'GET',
          url: '/Company/getCompanies',
          dataType: 'json',
        }, store);

        if (Array.isArray(response.data)) {
          this.companies = response.data.map((c) => ({
            ...c,
            is_active: c.is_active === true || c.is_active === 1 || c.is_active === '1',
            currency: c.currency || 'USD',
            company_name: c.company_name || '',
          }));

          this.totalRows = response.data.length;
          this.totalPages = Math.ceil(response.data.length / this.itemsPerPage);
          this.useLocalFiltering = true; // Default to local filtering
          this.persistState();
          return response.data;
        }
        throw new Error(response.message || 'Invalid company data');
      } catch (err) {
        this.setError(err, 'Failed to fetch companies', 'FETCH_COMPANIES_FAILED');
        throw err;
      } finally {
        this.loading = false;
      }
    },

    fetchCompaniesDebounced: debounce(function () {
      return this.fetchCompanies();
    }, 300),

    // Add local pagination actions
    setCurrentPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.persistState();
      }
    },
    
    setItemsPerPage(itemsPerPage) {
      this.itemsPerPage = itemsPerPage;
      this.currentPage = 1; // Reset to first page when changing page size
      this.totalPages = Math.ceil(this.companies.length / itemsPerPage);
      this.persistState();
    },

    async refreshCompanies() {
      this.loading = true;
      this.resetFilters();
      try {
        await this.fetchCompanies();
        this.currentPage = 1;
      } finally {
        this.loading = false;
      }
    },

    async getCompanyById(companyId) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
        return null;
      }

      // Handle "all" case - return all companies from local data
      if (companyId === 'all') {
        return;
      }

      // First check local store
      const cachedCompany = this.companies.find((c) => c.company_id === companyId);
      if (cachedCompany) {
        return { ...cachedCompany, currency: cachedCompany.currency || 'USD' };
      }

      // If not found locally, fetch from API
      this.loading = true;
      this.resetError();

      try {
        const response = await apiCall({
          method: 'GET',
          url: `/Company/getCompanyById/${companyId}`,
          dataType: 'json',
        }, store);

        if (response.data) {
          const company = {
            ...response.data,
            is_active: response.data.is_active === true || response.data.is_active === 1 || response.data.is_active === '1',
            currency: response.data.currency || 'USD',
            name: response.data.name || '',
          };
          this.companies.push(company);
          this.persistState();
          return company;
        }

        throw new Error(response.message || 'Company not found');
      } catch (err) {
        this.setError(err, 'Failed to fetch company', 'FETCH_COMPANY_FAILED');
        return null;
      } finally {
        this.loading = false;
      }
    },

    // CRUD operations
    async addCompany(companyData) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
        return;
      }
      
      // Check if user has reached the maximum number of companies
      /*if (this.companies.length >= this.maxCompaniesPerUser) {
        this.setError(
          null, 
          `Maximum limit of ${this.maxCompaniesPerUser} companies reached`, 
          'COMPANY_LIMIT_REACHED'
        );
        throw new Error(`You can only create up to ${this.maxCompaniesPerUser} companies`);
      }*/

      this.loading = true;
      this.resetError();
      
      try {
        const response = await apiCall({
          method: 'POST',
          url: '/Company/addCompany',
          data: companyData,
          dataType: 'json',
        }, store);

        if (response.status === 'success') {
          await this.fetchCompanies();
          useActivityStore().addActivity('COMPANY_CREATED', companyData.company_name);
          useSubscriptionStore().fetchUsage();
          return response;
        }

        throw new Error(response.message || 'Failed to add company');
      } catch (err) {
        this.setError(err, 'Failed to add company', 'ADD_COMPANY_FAILED');
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async updateCompany(companyData) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
        return;
      }
      this.loading = true;
      this.resetError();

      try {
        const response = await apiCall({
          method: 'POST',
          url: '/Company/updateCompany',
          data: companyData,
          dataType: 'json',
        }, store);

        if (response.status === 'success') {
          await this.fetchCompanies({ page: this.currentPage });

          useActivityStore().addActivity('COMPANY_UPDATED', companyData.company_name);

          if (this.currentCompany?.company_id === companyData.company_id) {
            this.currentCompany = {
              ...this.currentCompany,
              ...companyData,
            };
            this.persistState();
          }

          return response;
        }

        throw new Error(response.message || 'Failed to update company');
      } catch (err) {
        this.setError(err, 'Failed to update company', 'UPDATE_COMPANY_FAILED');
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async deleteCompany(companyId) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
        return;
      }
      this.loading = true;
      this.resetError();

      try {
        const response = await apiCall({
          method: 'POST',
          url: '/Company/deleteCompany',
          data: { companyId },
          dataType: 'json',
        }, store);

        if (response.status === 'success') {
          await this.fetchCompanies({ page: this.currentPage });
          useActivityStore().addActivity('COMPANY_DELETED');
          if (this.currentCompany?.company_id === companyId) {
            this.currentCompany = null;
            this.persistState();
          }

          return response;
        }

        throw new Error(response.message || 'Failed to delete company');
      } catch (err) {
        this.setError(err, 'Failed to delete company', 'DELETE_COMPANY_FAILED');
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async setCurrentCompany(companyId) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
        return;
      }
      this.loading = true;
      this.resetError();

      try {
        const company = this.companies.find((c) => c.company_id === companyId);
        if (company) {
          this.currentCompany = company;
          this.persistState();
          return;
        }

        const response = await apiCall({
          method: 'GET',
          url: `/Company/getCompany/${companyId}`,
          dataType: 'json',
        }, store);

        if (response.data) {
          this.currentCompany = response.data;
          this.persistState();
        }
      } catch (err) {
        this.setError(err, 'Failed to fetch current company', 'FETCH_COMPANY_FAILED');
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async toggleCompanyStatus(companyId, isActive) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
        return;
      }
      this.loading = true;
      this.resetError();

      try {
        const response = await apiCall({
          method: 'POST',
          url: '/Company/toggleCompanyStatus',
          data: { companyId, isActive },
          dataType: 'json',
        }, store);

        if (response.status === 'success') {
          const company = this.companies.find((c) => c.company_id === companyId);
          if (company) {
            company.is_active = isActive;
          }

          if (this.currentCompany?.company_id === companyId) {
            this.currentCompany = { ...this.currentCompany, is_active: isActive };
          }

          this.persistState();
          return response;
        }

        throw new Error(response.message || 'Failed to toggle status');
      } catch (err) {
        this.setError(err, 'Failed to toggle company status', 'TOGGLE_STATUS_FAILED');
        throw err;
      } finally {
        this.loading = false;
      }
    },
  },
});